import type React from 'react';
import type { WorkspaceFile } from '../../lib/workspace-files';
import { extensionOf } from '../../lib/artifact-file';
import { previewImageUrl, fileTypeKind } from '../../lib/artifact-image';
import { effectiveContent } from '../../lib/file-record';
import { filePreviewLines } from '../../lib/file-preview-text';

// The file's own graphic, and the ONE implementation of it. Everything that draws a file renders this — the
// "Agents at work" strip card, the narrow Apps & files card, and the Files list's thumbnails view — because a
// file that previews itself on one screen has to preview itself on the others. The Files grid drew a grey
// outline glyph of its own instead, so the same file read as two different things on two screens. Reported.
//
// Mirrors the native AAW card graphic.

/** The unscaled thumb height the strip card draws at. A bigger surface passes its own. */
export const THUMB_H = 54;

// The tilted page-card geometry the file-type icons are drawn at (Figma: −8°, rounded 4, soft shadow) — an
// image preview is clipped to the SAME footprint so previews and icons read identically. Mirrors native.
export const PAGE_TILT = -8;
export const PAGE_SHADOW = 'drop-shadow(0 3px 7px rgba(var(--ds-black-rgb), 0.18))';

/** The file thumb: a real image preview (clipped to the tilted page card) when the content is an image URL,
 *  else the file-type icon chosen by extension (PDF/Doc/Spreadsheet/… with a General fallback). Mirrors the
 *  native AAW card graphic. */
export function FileThumb({ item, basePath, height = THUMB_H }: { item: WorkspaceFile; basePath: string; height?: number }) {
  // `effectiveContent`, not `item.content`: an UPLOAD keeps its content empty by design and points at its
  // bytes through `referenceUrl`, so reading the raw field showed the file-type glyph for a picture. Reported.
  const preview = previewImageUrl(item);
  if (preview) {
    const width = Math.round(height * (41 / 54)); // the tilted page's w:h
    return (
      <div
        style={{
          width,
          height,
          borderRadius: 3,
          border: '1.5px solid var(--ds-white)',
          overflow: 'hidden',
          transform: `rotate(${PAGE_TILT}deg)`,
          filter: PAGE_SHADOW,
          background: 'rgba(var(--ds-black-rgb), 0.06)',
        }}
      >
        <img src={preview} alt="" style={{ width: '100%', height: '100%', objectFit: 'cover', objectPosition: 'top' }} />
      </div>
    );
  }
  // What the document SAYS, not what type it is. A generic glyph makes every report look alike until you
  // open one; the first lines are what tells them apart at a glance. Shared 1:1 with native.
  const lines = filePreviewLines(item.name, effectiveContent(item), item.contentType);
  if (lines) return <FilePreviewPage lines={lines} height={height} />;
  const kind = fileTypeKind(extensionOf(item));
  return (
    <img src={`${basePath}/file-types/${kind}.png`} alt="" height={height} style={{ height, width: 'auto', display: 'block', filter: PAGE_SHADOW }} />
  );
}

/**
 * The first lines of a document, as a page.
 *
 * `fill` is the grid-tile form: the preview IS the well — edge to edge, no tilt, no grey mat behind it —
 * because a document's own first lines are a better thumbnail than a shrunken page floating on a
 * placeholder background. The default is the compact tilted page, which is what a 26px chat chip or a
 * strip card has room for, and it matches the file-type glyph's footprint so the two are interchangeable.
 */
export function FilePreviewPage({ lines, height, fill = false }: { lines: string[]; height?: number; fill?: boolean }) {
  const box: React.CSSProperties = fill
    ? { width: '100%', height: '100%', borderRadius: 0, border: 'none', transform: 'none', filter: 'none' }
    : {
        width: Math.round((height ?? THUMB_H) * (41 / 54)),
        height: height ?? THUMB_H,
        borderRadius: 3,
        border: '1.5px solid var(--ds-white)',
        transform: `rotate(${PAGE_TILT}deg)`,
        filter: PAGE_SHADOW,
      };
  // The type scale follows the box, so one component serves a chat chip and a gallery tile.
  const base = fill ? 11 : Math.max(2, (height ?? THUMB_H) / 22);
  return (
    <div
      aria-hidden
      style={{
        ...box,
        background: 'var(--ds-white)',
        overflow: 'hidden',
        padding: fill ? 12 : Math.max(2, (height ?? THUMB_H) / 14),
        display: 'flex',
        flexDirection: 'column',
        gap: fill ? 3 : 1,
        boxSizing: 'border-box',
      }}
    >
      {lines.map((line, i) => (
        <span
          key={i}
          style={{
            fontSize: base,
            lineHeight: 1.35,
            color: 'var(--ds-text-secondary)',
            whiteSpace: 'nowrap',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
          }}
        >
          {line}
        </span>
      ))}
    </div>
  );
}

/** A small floating "dashboard/app" illustration — a tilted white page holding a few colored stat tiles. */
export function AppThumb({ height = THUMB_H }: { height?: number }) {
  const width = Math.round(height * (132 / 118));
  return (
    <svg width={width} height={height} viewBox="0 0 132 118" fill="none" aria-hidden style={{ transform: 'rotate(-4deg)', filter: 'drop-shadow(0 3px 7px rgba(var(--ds-black-rgb), 0.18))' }}>
      <rect x="1" y="1" width="130" height="116" rx="11" fill="var(--ds-white)" stroke="rgba(var(--ds-black-rgb), 0.08)" />
      <rect x="14" y="16" width="48" height="34" rx="7" fill="var(--ds-lime-200)" />
      <rect x="70" y="16" width="48" height="34" rx="7" fill="var(--ds-lime-300)" />
      <rect x="14" y="58" width="48" height="34" rx="7" fill="var(--ds-pink-400)" />
      <rect x="70" y="58" width="48" height="34" rx="7" fill="var(--ds-lime-200)" />
    </svg>
  );
}

